import { useState } from "react";
import { motion } from "framer-motion";
import { imageUrl, money, api, API } from "@/lib/api";
import { useCountdown, pad } from "@/lib/useCountdown";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Gavel, LogIn, Info, CreditCard, Share2 } from "lucide-react";

const FREE_SHIP_THRESHOLD = 450;
const FLAT_SHIPPING = 50;

function Timer({ endsAt }) {
  const t = useCountdown(endsAt);
  if (t.ended) return <span className="font-mono text-reefpink text-sm tracking-widest">AUCTION ENDED</span>;
  const seg = (v, l) => (
    <div className="text-center">
      <div className="font-mono text-2xl md:text-3xl text-toxic leading-none">{pad(v)}</div>
      <div className="mono-label text-[0.55rem] text-white/40 mt-1">{l}</div>
    </div>
  );
  return (
    <div className="flex items-center gap-3 md:gap-4">
      {seg(t.d, "Days")}<span className="text-white/20">:</span>{seg(t.h, "Hrs")}<span className="text-white/20">:</span>{seg(t.m, "Min")}<span className="text-white/20">:</span>{seg(t.s, "Sec")}
    </div>
  );
}

export function AuctionCard({ auction, onUpdate, onRequireAuth, index = 0 }) {
  const t = useCountdown(auction.ends_at);
  const { user } = useAuth();
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [paying, setPaying] = useState(false);
  const minNext = auction.bids?.length ? auction.current_bid + auction.bid_increment : auction.current_bid;

  const submit = async () => {
    if (!user) { onRequireAuth?.(); return; }
    const amt = parseFloat(amount);
    if (!amt || amt < minNext) return toast.error(`Your maximum must be at least ${money(minNext)}`);
    setBusy(true);
    try {
      const { data } = await api.post(`/auctions/${auction.id}/bid`, { max_amount: amt });
      if (data.you_lead) {
        toast.success(`You're the top bidder! Price is ${money(data.current_bid)} — we'll auto-bid for you up to ${money(amt)}.`);
      } else {
        toast.error(`Outbid — the current leader has a higher max. Price is now ${money(data.current_bid)}. Raise your max to lead.`);
      }
      setAmount("");
      onUpdate?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place bid");
    } finally { setBusy(false); }
  };

  const live = !t.ended;
  const youAreLeader = user && auction.top_bidder_email && user.email === auction.top_bidder_email;
  const youWon = !live && youAreLeader && auction.bids?.length > 0;
  const winShipping = auction.current_bid >= FREE_SHIP_THRESHOLD ? 0 : FLAT_SHIPPING;
  const winTotal = auction.current_bid + winShipping;

  const payNow = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/auctions/${auction.id}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setPaying(false);
    }
  };

  const shareAuction = async () => {
    const url = `${API}/share/auction/${auction.id}`;
    try {
      if (navigator.share) await navigator.share({ title: `${auction.name} — Live Auction`, text: `Bid on ${auction.name} at Exotic Reefing Inc`, url });
      else { await navigator.clipboard.writeText(url); toast.success("Share link copied — paste it anywhere!"); }
    } catch (e) { /* cancelled */ }
  };

  return (
    <motion.div
      data-testid={`auction-card-${auction.id}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (index % 2) * 0.1 }}
      className={`grid md:grid-cols-2 rounded-2xl overflow-hidden border ${live ? "border-toxic/30" : "border-white/10"} bg-white/[0.02]`}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img src={imageUrl(auction.image)} alt={auction.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-void/60" />
        <span className={`absolute top-4 left-4 mono-label px-3 py-1 rounded-full border ${live ? "text-toxic border-toxic/50 bg-void/70" : "text-white/50 border-white/20 bg-void/70"}`}>
          {live && <span className="inline-block w-1.5 h-1.5 rounded-full bg-toxic mr-2 animate-pulse" />}
          {live ? "Live" : "Closed"} · {auction.category}
        </span>
        <button data-testid={`share-auction-${auction.id}`} onClick={shareAuction}
          title="Share" aria-label="Share this auction"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-void/70 border border-white/15 flex items-center justify-center text-white/70 hover:text-cyan hover:border-cyan/50 transition-colors">
          <Share2 size={15} />
        </button>
      </div>

      <div className="p-6 md:p-8 flex flex-col">
        <p className="text-white/40 font-mono text-xs italic">{auction.species}</p>
        <h3 className="font-display text-3xl leading-tight mb-2">{auction.name}</h3>
        <p className="text-white/60 text-sm md:text-base mb-6 leading-relaxed">{auction.description}</p>

        <div className="mb-5"><Timer endsAt={auction.ends_at} /></div>

        <div className="flex items-end justify-between mb-5 pt-4 border-t border-white/10">
          <div>
            <p className="mono-label text-white/40">Current Bid</p>
            <motion.p
              key={auction.current_bid}
              initial={{ scale: 1.25, color: "#E2FF3D" }}
              animate={{ scale: 1, color: "#00F0FF" }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="font-mono text-3xl"
              data-testid={`current-bid-${auction.id}`}>
              {money(auction.current_bid)}
            </motion.p>
          </div>
          <div className="text-right">
            <p className="mono-label text-white/40">Top Bidder</p>
            <p className="font-mono text-sm text-white/80" data-testid={`top-bidder-${auction.id}`}>{auction.top_bidder || "—"}</p>
            <p className="text-white/30 text-xs mt-1">{auction.bids?.length || 0} bids</p>
          </div>
        </div>

        {auction.bids?.length > 0 && (
          <div className="mb-5 rounded-xl border border-white/10 bg-white/[0.02] divide-y divide-white/5" data-testid={`bid-history-${auction.id}`}>
            <p className="mono-label text-white/40 px-4 pt-3 pb-2">Bid activity</p>
            {auction.bids.slice(0, 4).map((b, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-2 text-sm">
                <span className={`truncate ${i === 0 ? "text-white" : "text-white/60"}`}>
                  {i === 0 && <span className="text-cyan mr-1.5">▲</span>}{b.bidder_name}
                </span>
                <span className="font-mono text-cyan">{money(b.amount)}</span>
              </div>
            ))}
          </div>
        )}

        {live ? (
          <div className="mt-auto">
            {youAreLeader && (
              <p className="mono-label text-cyan mb-3">★ You're the top bidder</p>
            )}
            <div className="flex flex-col sm:flex-row gap-3">
              {user ? (
                <>
                  <input data-testid={`bid-amount-${auction.id}`} type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`Max bid ≥ ${minNext}`}
                    className="bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm font-mono focus:border-cyan outline-none flex-1" />
                  <button data-testid={`place-bid-${auction.id}`} onClick={submit} disabled={busy}
                    className="btn-pill btn-pink flex items-center justify-center gap-2 disabled:opacity-50 whitespace-nowrap">
                    <Gavel size={15} /> Bid
                  </button>
                </>
              ) : (
                <button data-testid={`signin-to-bid-${auction.id}`} onClick={() => onRequireAuth?.()}
                  className="btn-pill btn-solid flex items-center justify-center gap-2 w-full">
                  <LogIn size={15} /> Sign in / register to bid
                </button>
              )}
            </div>
            <p className="text-white/35 text-xs mt-3 flex items-start gap-1.5">
              <Info size={13} className="mt-0.5 shrink-0" /> Enter your <b className="text-white/60 mx-1">maximum</b> bid. We auto-bid the minimum needed to keep you on top, up to your max.
            </p>
          </div>
        ) : (
          <div className="mt-auto">
            {youWon ? (
              auction.paid ? (
                <p className="mono-label text-cyan" data-testid={`auction-paid-${auction.id}`}>✓ Paid — thank you! We'll arrange delivery.</p>
              ) : (
                <>
                  <p className="mono-label text-cyan mb-2">★ You won this auction!</p>
                  <div className="rounded-xl border border-cyan/20 bg-cyan/[0.04] px-4 py-3 mb-3 text-sm text-white/70 font-mono" data-testid={`auction-total-${auction.id}`}>
                    Winning bid {money(auction.current_bid)} · {winShipping === 0 ? "FREE shipping" : `+ ${money(winShipping)} shipping`} · Total <b className="text-cyan">{money(winTotal)}</b>
                  </div>
                  <button data-testid={`pay-auction-${auction.id}`} onClick={payNow} disabled={paying}
                    className="btn-pill btn-solid w-full flex items-center justify-center gap-2 disabled:opacity-50">
                    <CreditCard size={15} /> {paying ? "Redirecting…" : `Pay ${money(winTotal)} & checkout`}
                  </button>
                  <p className="text-white/30 text-xs mt-2 text-center font-mono">Secured by Stripe · $50 flat shipping, FREE over $450</p>
                </>
              )
            ) : (
              <p className="text-white/40 text-sm font-mono" data-testid={`auction-closed-${auction.id}`}>
                {auction.top_bidder ? `Won by ${auction.top_bidder}.` : "Auction closed with no bids."}
                {auction.paid ? " Paid." : (auction.top_bidder ? " Winner can pay online here." : "")}
              </p>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}
